import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button, DialogContent } from '@mui/material';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import VpnLockOutlinedIcon from '@mui/icons-material/VpnLockOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import FlagCircleOutlinedIcon from '@mui/icons-material/FlagCircleOutlined';
import styles from './AddTemplateDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import InputField from '../atoms/InputField';
import { FitnessGoalOptions, TRAINING_LEVELS } from '../../constants';
import { strToOptionObj } from '../../utils/utils';
import { getEmptyTemplate } from '../../utils/templates';

import type { TrainingRoutineTemplate, WithOptionalId } from '../../types';

export interface AddTemplateDialogProps {
  originalTemplate?: TrainingRoutineTemplate;
  title: string;
  onClose: () => void;
  addTemplate: (newTemplate: WithOptionalId<TrainingRoutineTemplate>) => void;
  isOpen: boolean;
  trainerId: string;
}

const AddTemplateDialog: React.FC<AddTemplateDialogProps> = ({
  title,
  isOpen,
  onClose,
  trainerId,
  addTemplate,
  originalTemplate,
}) => {
  const [name, setName] = useState('');
  const [level, setLevel] = useState(0);
  const [fitnessGoal, setFitnessGoal] = useState<string>('');
  const [isPrivate, setIsPrivate] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [fitnessGoalError, setFitnessGoalError] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setLevel(0);
      setFitnessGoal('');
      setIsPrivate(true);
      setNameError(false);
      setFitnessGoalError(false);
    }
  }, [isOpen, originalTemplate]);

  const handleSubmit = async () => {
    if (!name) return setNameError(true);
    const isPublic = !isPrivate;
    if (isPublic && !fitnessGoal) return setFitnessGoalError(true);

    const newTemplate = getEmptyTemplate(
      name,
      level,
      fitnessGoal,
      isPrivate,
      trainerId,
    );
    addTemplate(newTemplate);
    onClose();
  };

  return (
    <BaseDialog
      onClose={onClose}
      isOpen={isOpen}
      title={title}
      actions={
        <Button className={styles.submit} onClick={handleSubmit}>
          <span className={styles.submitText}>Create Workout</span>
        </Button>
      }
    >
      <DialogContent className={styles.templateDataDialog}>
        <div className={styles.row}>
          <InputField
            placeholder="Add A Workout Name"
            icon={<EditNoteOutlinedIcon color="primary" />}
            title="Workout Name"
            inputName="workoutName"
            value={name}
            onChange={(value) => {
              setName(value);
              setNameError(false);
            }}
            error={nameError}
          />
          <InputField
            title="Workout Level"
            inputName="workoutLevel"
            icon={<SpeedOutlinedIcon color="primary" />}
            value={level}
            options={TRAINING_LEVELS.map((v, i) => ({ label: v, value: i }))}
            onChange={(newLevel) => {
              const numericLevel = Number(newLevel);
              setLevel(numericLevel);
            }}
          />
        </div>
        <div className={styles.row}>
          <InputField
            placeholder="Choose Fitness Goal"
            title="Fitness Goal"
            icon={<FlagCircleOutlinedIcon color="primary" />}
            inputName="fitnessGoal"
            options={FitnessGoalOptions}
            value={fitnessGoal}
            onChange={(value) => {
              setFitnessGoal(value);
              setFitnessGoalError(false);
            }}
            error={fitnessGoalError}
          />
          <InputField
            icon={<VpnLockOutlinedIcon color="primary" />}
            placeholder="Privacy Setting"
            title="Privacy"
            inputName="privacy"
            value={
              _.isBoolean(isPrivate) ? (isPrivate ? 'Private' : 'Public') : ''
            }
            options={['Public', 'Private'].map((val) => strToOptionObj(val))}
            onChange={(value) => {
              setIsPrivate(value === 'Private');
            }}
          />
        </div>
      </DialogContent>
    </BaseDialog>
  );
};

export default AddTemplateDialog;
